import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

/* ─────────────────────────────────────────────────────────────
 * GLOBAL SAFETY NET for browser-extension noise.
 *
 * Certain extensions (Edge Copilot / Web Context, Chrome Translate,
 * some ad-blockers, ChatGPT / Claude browser helpers) wrap the global
 * `fetch()` and then try to forward the Request object to their side
 * panel via `postMessage`. Request objects aren't structured-cloneable,
 * so the clone throws — the wrapped fetch promise rejects with exactly:
 *   "Failed to execute 'postMessage' on 'Window': Request object could
 *    not be cloned."
 * We treat this as benign noise and transparently retry via XHR so the
 * app keeps working no matter which extension is running.
 * ─────────────────────────────────────────────────────────────*/

const NOISE_RE = /could not be cloned|postMessage/i;

// 1) Swallow noise when it surfaces through window.postMessage itself.
const originalPostMessage = window.postMessage;
window.postMessage = function (message, targetOrigin, transfer) {
  try {
    return originalPostMessage.call(this, message, targetOrigin, transfer);
  } catch (e) {
    if (e?.message && (NOISE_RE.test(e.message) || e.message.includes("PerformanceServerTiming"))) return;
    throw e;
  }
};

// 2) Swallow noise that bubbles to the global error handlers.
window.addEventListener("unhandledrejection", (ev) => {
  const msg = ev?.reason?.message || String(ev?.reason || "");
  if (NOISE_RE.test(msg)) ev.preventDefault();
});
window.addEventListener("error", (ev) => {
  if (ev?.message && NOISE_RE.test(ev.message)) ev.preventDefault();
});

// 3) GLOBAL fetch wrapper — transparent XHR fallback on extension clone errors.
//    Every `fetch(...)` in the codebase is now immune.
const nativeFetch = window.fetch.bind(window);

function xhrFallback(input, init = {}) {
  return new Promise((resolve, reject) => {
    try {
      const url = typeof input === "string" ? input : input?.url;
      if (!url) return reject(new Error("xhrFallback: missing url"));
      const method = (init.method || (typeof input === "object" && input.method) || "GET").toUpperCase();
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      // Copy headers — support plain object, Headers, and arrays.
      const hdrs = init.headers || (typeof input === "object" && input.headers) || {};
      if (hdrs && typeof hdrs.forEach === "function") {
        hdrs.forEach((v, k) => xhr.setRequestHeader(k, v));
      } else if (Array.isArray(hdrs)) {
        hdrs.forEach(([k, v]) => xhr.setRequestHeader(k, v));
      } else if (hdrs && typeof hdrs === "object") {
        Object.entries(hdrs).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      }
      if (init.credentials === "include") xhr.withCredentials = true;
      xhr.responseType = "text";
      xhr.onload = () => {
        const body = xhr.responseText;
        // Build a minimal Response-like shim used by `await res.json()` / `.text()` / `.ok`.
        const response = {
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          statusText: xhr.statusText,
          url,
          headers: {
            get: (name) => xhr.getResponseHeader(name),
            forEach: () => {},
          },
          text: async () => body,
          json: async () => {
            try { return JSON.parse(body); }
            catch { throw new Error("xhrFallback: response is not JSON"); }
          },
          clone() { return response; },
        };
        resolve(response);
      };
      xhr.onerror = () => reject(new TypeError("Network request failed"));
      xhr.ontimeout = () => reject(new TypeError("Network request timed out"));
      const body = init.body ?? (typeof input === "object" ? input.body : undefined);
      xhr.send(body || null);
    } catch (e) {
      reject(e);
    }
  });
}

window.fetch = function patchedFetch(input, init) {
  return nativeFetch(input, init).catch((err) => {
    const msg = err?.message || "";
    if (NOISE_RE.test(msg)) {
      // Benign extension clone failure — retry via XHR.
      return xhrFallback(input, init);
    }
    throw err;
  });
};

/* ─────────────────────────────────────────────────────────── */

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
